(function () {
  const STORAGE_KEY = 'sleek:wishlist';

  // Register a pubsub event so buttons, the header badge and the grid stay in sync.
  if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
    FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate =
      FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate || 'wishlist-update';
  }

  const Wishlist = {
    get available() {
      return !!(window.FoxTheme && FoxTheme.config && FoxTheme.config.hasLocalStorage);
    },

    get() {
      if (!this.available) return [];
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    },

    save(items) {
      if (!this.available) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (err) {}
      if (window.FoxTheme && FoxTheme.pubsub) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate, { items });
      }
    },

    has(handle) {
      return this.get().indexOf(handle) !== -1;
    },

    toggle(handle) {
      const items = this.get();
      const index = items.indexOf(handle);
      if (index === -1) {
        items.push(handle);
      } else {
        items.splice(index, 1);
      }
      this.save(items);
      return index === -1; // true when added
    },

    remove(handle) {
      const items = this.get();
      const index = items.indexOf(handle);
      if (index === -1) return;
      items.splice(index, 1);
      this.save(items);
    },
  };

  // Expose for debugging / reuse.
  if (window.FoxTheme) FoxTheme.wishlist = Wishlist;

  /* ───────────────────────── Heart toggle button ───────────────────────── */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('button') || this;
      this.onClick = this.onClick.bind(this);
      this.onUpdate = this.onUpdate.bind(this);

      this.button.addEventListener('click', this.onClick);
      this.render();

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate,
          this.onUpdate
        );
      }
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.unsubscribe) this.unsubscribe();
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      Wishlist.toggle(this.handle);
    }

    onUpdate() {
      this.render();
    }

    render() {
      const active = Wishlist.has(this.handle);
      this.classList.toggle('is-active', active);
      if (this.button && this.button !== this) {
        this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
      const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label && this.button) this.button.setAttribute('aria-label', label);
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  /* ───────────────────────── Header count badge ───────────────────────── */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onUpdate = this.onUpdate.bind(this);
      this.update(Wishlist.get());

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate,
          this.onUpdate
        );
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    onUpdate(event) {
      this.update((event && event.items) || Wishlist.get());
    }

    update(items) {
      const count = items.length;
      if (count > 99) {
        this.innerHTML = '<span class="text-sm">99+</span>';
        this.classList.add('cart-count--small-medium');
      } else {
        this.textContent = count;
        this.classList.remove('cart-count--small-medium');
      }
      this.hidden = count === 0;
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* ───────────────────────── Wishlist page grid ───────────────────────── */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.cardSection = this.dataset.cardSection;
      this.emptyEl = document.querySelector('[data-wishlist-empty]');
      this.onUpdate = this.onUpdate.bind(this);
      this.currentHandles = '';

      this.build();

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.wishlistUpdate,
          this.onUpdate
        );
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    onUpdate() {
      // Only rebuild if the set of handles actually changed (e.g. item removed here).
      if (Wishlist.get().join(',') !== this.currentHandles) this.build();
    }

    build() {
      const handles = Wishlist.get();
      this.currentHandles = handles.join(',');

      if (!handles.length) {
        this.innerHTML = '';
        this.hidden = true;
        if (this.emptyEl) this.emptyEl.hidden = false;
        return;
      }

      this.hidden = false;
      if (this.emptyEl) this.emptyEl.hidden = true;

      Promise.all(handles.map((handle) => this.fetchCard(handle))).then((cards) => {
        this.innerHTML = '';
        cards.forEach((markup) => {
          if (!markup) return;
          const column = document.createElement('div');
          column.className = 'f-column card';
          column.innerHTML = markup;
          this.appendChild(column);
        });
      });
    }

    fetchCard(handle) {
      const url = '/products/' + encodeURIComponent(handle) + '?section_id=' + this.cardSection;
      return fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          if (!text) return '';
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.shopify-section');
          return section ? section.innerHTML : '';
        })
        .catch(() => '');
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);
})();
